import { FC } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

export interface DynamicListItem {
  id: number;
  value: string;
}

interface IDynamicListFieldProps {
  items: DynamicListItem[];
  onChange: (id: number, value: string) => void;
  onAdd: () => void;
  onRemove: (id: number) => void;
  /** Whether the add button is shown (e.g. below a max count). */
  canAdd: boolean;
  addLabel: string;
  placeholder: string;
  /** Per-input max character length. */
  maxLength?: number;
  /** Optional per-row normalizer, applied when the input loses focus. */
  onBlur?: (id: number, value: string) => void;
  /** When set, prefixes each row with "<label> N" (e.g. "Pattern 1"). */
  patternLabel?: string;
  /** Decides per-row whether the remove button shows. Defaults to "more than one row". */
  showRemove?: (item: DynamicListItem, length: number) => boolean;
}

/**
 * Repeatable text-input list (add / edit / remove rows). Replaces the three
 * hand-rolled editors in Settings (page-targeting routes, suppress URLs,
 * engagement prompts) that shared identical add/change/remove plumbing.
 */
const DynamicListField: FC<IDynamicListFieldProps> = ({
  items,
  onChange,
  onAdd,
  onRemove,
  canAdd,
  addLabel,
  placeholder,
  maxLength,
  onBlur,
  patternLabel,
  showRemove,
}) => {
  const shouldShowRemove = (item: DynamicListItem) =>
    showRemove ? showRemove(item, items.length) : items.length > 1;

  return (
    <div className='space-y-2'>
      {items.map((item, index) => (
        <div key={item.id} className='flex items-center gap-2'>
          {patternLabel && (
            <span className='text-xs text-muted-foreground shrink-0 w-20'>
              {patternLabel} {index + 1}
            </span>
          )}
          <Input
            placeholder={placeholder}
            value={item.value}
            maxLength={maxLength}
            onChange={(e) => onChange(item.id, e.target.value)}
            onBlur={onBlur ? (e) => onBlur(item.id, e.target.value) : undefined}
          />
          {shouldShowRemove(item) && (
            <Button
              variant='ghost'
              size='icon'
              className='shrink-0 h-8 w-8 text-muted-foreground hover:text-destructive'
              onClick={() => onRemove(item.id)}
            >
              <X className='w-4 h-4' />
            </Button>
          )}
        </div>
      ))}
      {canAdd && (
        <Button variant='outline' size='sm' onClick={onAdd}>
          {addLabel}
        </Button>
      )}
    </div>
  );
};

export default DynamicListField;
